// Decides, in one place, whether a /stripe_setup response can be handed to Stripe Elements.
//
// The old caller checked `json.success === false` and nothing else. Anything that did not have that
// exact shape — a 500 that still renders JSON, a proxy or WAF error body, a 2xx missing
// client_secret — fell through as a success and reached Elements with `undefined` keys. That throws
// inside a promise nobody awaited, so the buyer got no error and no card fields: just the dimmed,
// permanently loading checkout this exists to prevent.
//
// Kept free of DOM and network so it can be unit-tested (stripe_setup_outcome.test.mjs).

export const GENERIC_SETUP_ERROR =
  'We could not reach the payment processor. Please try again.'

// Returns the message to show the buyer, or null when the response is safe to mount.
export function stripeSetupError(responseOk, json) {
  const body = json && typeof json === 'object' ? json : {}

  // The server's own message wins whenever it sent one — it is the specific, buyer-safe wording
  // (unusable key pair, invalid amount, no payment method available).
  if (!responseOk || body.success === false) {
    return body.alert || GENERIC_SETUP_ERROR
  }

  // A 2xx that cannot mount is still a failure, and a silent one if we let it through.
  if (!body.publishable_key || !body.client_secret) {
    return GENERIC_SETUP_ERROR
  }

  return null
}

// The only failure the cart may take back. The watchdog is a guess — 25 seconds with no word from
// Stripe — and on a slow enough connection it fires on a form that was merely still loading.
// Everything else is something we were actually told.
export const RECOVERABLE_FAILURE = 'watchdog'

// Whether a newly reported failure should replace one already recorded.
//
// Nothing downgrades: once Stripe has explicitly said a mount failed, a later watchdog cannot soften
// it. But an explicit failure arriving *after* a watchdog must be able to upgrade it, or the
// sequence watchdog -> loaderror -> ready would recover a mount Stripe had told us was dead — the
// first failure would have stuck as 'watchdog', which is the one kind the recovery path lets
// through.
export function replacesFailure(existingKind, newKind) {
  if (!existingKind) return true
  if (newKind === RECOVERABLE_FAILURE) return false

  return existingKind === RECOVERABLE_FAILURE
}
